import { useState } from "react";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { formatHours } from "@/lib/format";
import { TONE_SURFACE, TONE_TEXT } from "@/lib/widget-status";
import { useDeleteTimeEntry, useUpdateTimeEntry } from "@/hooks/use-time-registration";
import { toast } from "sonner";
import SmartTimeInput from "@/components/time-registration/SmartTimeInput";
import type { DayOverview } from "@/hooks/use-month-time-overview";
import type { TimeGap } from "@/hooks/use-time-gaps";
import { SrIcon } from "@/components/ui/SrIcon";
import { DAY_TONE, dayRowId, getDayStatus, type DayStatus } from "./day-status";

interface EditState {
  entryId: string;
  from: string;
  to: string;
}

function extractTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function computeEndTime(startDate: string, durationSeconds: number): string {
  const d = new Date(new Date(startDate).getTime() + durationSeconds * 1000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function hasTimeComponent(dateStr: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0;
}

function formatEntryTime(start: string, durationSeconds: number): string {
  if (!hasTimeComponent(start)) return "";
  const startDate = new Date(start);
  const endDate = new Date(startDate.getTime() + durationSeconds * 1000);
  const fmt = (d: Date) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

function surfaceClass(status: DayStatus): string {
  if (status === "absence" || status === "holiday") return "bg-muted border-border/50";
  if (status === "partial" || status === "missing") return TONE_SURFACE[DAY_TONE[status]!];
  return "bg-card border-border/50";
}

function hoursClass(status: DayStatus): string {
  const tone = DAY_TONE[status];
  return tone ? TONE_TEXT[tone] : "text-muted-foreground";
}

interface CloseMonthDayRowProps {
  day: DayOverview;
  gap?: TimeGap;
  expanded: boolean;
  onToggle: () => void;
  onAddEntry?: (date: string) => void;
}

export default function CloseMonthDayRow({ day, gap, expanded, onToggle, onAddEntry }: CloseMonthDayRowProps) {
  const [editState, setEditState] = useState<EditState | null>(null);
  const { mutate: deleteEntry } = useDeleteTimeEntry();
  const { mutate: updateEntry, isPending: isSaving } = useUpdateTimeEntry();

  const status: DayStatus = gap ? getDayStatus(gap) : day.totalHours > 0 ? "ok" : "missing";
  const dateObj = parseISO(day.date);
  const absencePrefix = gap?.absenceHours && status !== "absence" ? `Fravær ${formatHours(gap.absenceHours)} · ` : "";
  const projects = [...new Set(day.entries.map((e) => e.task_description.split(" - ")[0]))].join(", ");

  const handleSaveEdit = (entry: { id: string; start_date: string }) => {
    if (!editState) return;
    const [fh, fm] = editState.from.split(":").map(Number);
    const [th, tm] = editState.to.split(":").map(Number);
    if (isNaN(fh) || isNaN(th)) {
      toast.error("Ugyldig klokkeslett");
      return;
    }
    const diffMins = th * 60 + tm - fh * 60 - fm;
    if (diffMins <= 0) {
      toast.error("Til må være etter fra");
      return;
    }
    const dateStr = entry.start_date.split("T")[0];
    const newStart = new Date(`${dateStr}T${editState.from}:00`).toISOString();
    updateEntry(
      {
        entryId: entry.id,
        updates: { start_date: newStart, duration_seconds: diffMins * 60 },
      },
      { onSuccess: () => setEditState(null) }
    );
  };

  const addButton = (className: string) =>
    onAddEntry && (
      <button
        type="button"
        className={className}
        onClick={(e) => {
          e.stopPropagation();
          onAddEntry(day.date);
        }}
      >
        <SrIcon name="plus" className="w-4 h-4 xl:w-3 xl:h-3" />
        Ny registrering
      </button>
    );

  return (
    <div id={dayRowId(day.date)} className={cn("rounded-lg border transition-colors scroll-mt-4", surfaceClass(status))}>
      {/* Day row */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 px-4 py-3 text-left min-h-[52px] cursor-pointer"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex flex-col xl:flex-row xl:items-center xl:gap-0 flex-shrink-0 xl:w-[104px]">
          <span className="text-xs xl:text-sm text-muted-foreground font-medium capitalize xl:w-10">
            {format(dateObj, "EEE", { locale: nb })}
          </span>
          <span className="text-sm font-medium xl:w-16">
            {format(dateObj, "d. MMM", { locale: nb })}
          </span>
        </div>
        <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate xl:px-3">
          {status === "absence" ? (
            "Fravær"
          ) : status === "holiday" ? (
            "Helligdag"
          ) : status === "missing" ? (
            <>
              {absencePrefix}
              <span className={TONE_TEXT.miss}>Ingen timer registrert</span>
            </>
          ) : (
            <>
              {absencePrefix}
              {projects}
              {status === "partial" && gap && (
                <>
                  {" · "}
                  <span className={TONE_TEXT.near}>mangler {formatHours(gap.gap)}</span>
                </>
              )}
            </>
          )}
        </span>
        {status === "missing" &&
          !expanded &&
          addButton(
            "hidden xl:inline-flex items-center gap-1.5 text-xs text-muted-foreground px-3 py-1 rounded border border-dashed border-border/50 bg-card hover:bg-muted/50 transition-colors flex-shrink-0"
          )}
        <span className={cn("text-sm font-semibold text-right whitespace-nowrap flex-shrink-0", hoursClass(status))}>
          {status === "absence" || status === "holiday" ? "—" : formatHours(day.totalHours)}
        </span>
        <span className="text-muted-foreground flex-shrink-0">
          {expanded ? (
            <SrIcon name="chevron-up" className="w-4 h-4"  />
          ) : (
            <SrIcon name="chevron-down" className="w-4 h-4"  />
          )}
        </span>
      </div>

      {/* Mobil: tom dag får knappen synlig uten å åpne raden */}
      {status === "missing" && !expanded && (
        <div className="xl:hidden px-4 pb-3 -mt-1">
          {addButton(
            "w-full flex items-center justify-center gap-1.5 text-sm text-muted-foreground px-3 py-2.5 rounded border border-dashed border-border bg-card"
          )}
        </div>
      )}

      {/* Expanded entries */}
      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-border/30 space-y-1">
          {day.entries.length === 0 ? (
            <p className="text-xs text-muted-foreground py-1">
              Ingen timer registrert denne dagen.
            </p>
          ) : (
            day.entries.map((entry) => {
              const hours =
                entry.manual_hours_override ??
                entry.duration_seconds / 3600;
              const timeRange = formatEntryTime(
                entry.start_date,
                entry.duration_seconds
              );
              const isEditing = editState?.entryId === entry.id;
              const hasTime = hasTimeComponent(entry.start_date);

              if (isEditing) {
                return (
                  <div
                    key={entry.id}
                    className="rounded px-3 py-3 bg-muted/40 border border-border/50 space-y-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="text-xs text-muted-foreground break-words">
                      {entry.task_description}
                    </p>
                    <div className="flex items-center gap-2">
                      <SmartTimeInput
                        value={editState!.from}
                        onChange={(v) => setEditState({ ...editState!, from: v })}
                        placeholder="Fra"
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">–</span>
                      <SmartTimeInput
                        value={editState!.to}
                        onChange={(v) => setEditState({ ...editState!, to: v })}
                        placeholder="Til"
                        className="flex-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditState(null);
                        }}
                        className="flex-1 h-10 flex items-center justify-center gap-1 rounded-lg border border-border bg-card text-sm"
                      >
                        <SrIcon name="x" className="w-4 h-4"  /> Avbryt
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveEdit(entry);
                        }}
                        disabled={isSaving}
                        className="flex-1 h-10 flex items-center justify-center gap-1 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-60"
                      >
                        <SrIcon name="check" className="w-4 h-4"  /> Lagre
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={entry.id}
                  className="flex flex-col xl:flex-row xl:items-center gap-1 xl:gap-0 text-xs py-2 xl:py-1.5 rounded px-2 bg-background/50 group/entry"
                >
                  <span className="text-foreground xl:flex-1 xl:truncate xl:order-2 break-words">
                    {entry.task_description}
                  </span>
                  <div className="flex items-center gap-2 xl:contents">
                    <span className="text-muted-foreground xl:w-[90px] xl:order-1">
                      {timeRange || "—"}
                    </span>
                    <span className="font-medium text-foreground ml-auto xl:ml-2 whitespace-nowrap xl:order-3">
                      {formatHours(hours)}
                    </span>
                    <div className="flex gap-0.5 xl:ml-2 xl:order-4 xl:opacity-0 xl:group-hover/entry:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditState({
                            entryId: entry.id,
                            from: hasTime ? extractTime(entry.start_date) : "",
                            to: hasTime ? computeEndTime(entry.start_date, entry.duration_seconds) : "",
                          });
                        }}
                        className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-muted rounded"
                        aria-label="Rediger"
                      >
                        <SrIcon name="pencil" className="w-4 h-4 xl:w-3 xl:h-3 text-muted-foreground"  />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteEntry(entry.id, {
                            onSuccess: () => toast.success("Registrering slettet"),
                          });
                        }}
                        className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-destructive/10 rounded"
                        aria-label="Slett"
                      >
                        <SrIcon name="trash2" className="w-4 h-4 xl:w-3 xl:h-3 text-destructive"  />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {addButton(
            "mt-2 w-full xl:w-auto xl:inline-flex flex items-center justify-center gap-1.5 text-sm xl:text-xs text-muted-foreground px-3 py-2.5 xl:py-1 rounded border border-dashed border-border/50 hover:bg-muted/50 transition-colors"
          )}
        </div>
      )}
    </div>
  );
}
